import { FormEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SideDrawer, withStyles } from 'vopay';
import { Button, MaterialSymbol, Title, UtilButton } from 'UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import useIntegrationGroupLabels from '../useIntegrationGroupLabels';
import { IntegrationCardData, IntegrationSetup } from '../types';
import IntegrationHowItWorksDialog from './IntegrationHowItWorksDialog';
import IntegrationWorkflowsDialog from './IntegrationWorkflowsDialog';
import useIntegrationSetupFormConfig, { IntegrationSetupFormValues } from './useIntegrationSetupFormConfig';
import useSaveIntegrationCredentials from './useSaveIntegrationCredentials';
import styles from './IntegrationSetupDrawer.module.scss';

const cn = withStyles(styles);

const SETUP_FORM_ID = 'integrationSetupForm';

/**
 * locked — connected integration, saved details hidden; Update Details asks before editing
 * edit   — fields open; Review locks them for a last look
 * review — fields read back; Connect saves
 */
type SetupStep = 'locked' | 'edit' | 'review';

type IntegrationSetupDrawerProps = {
  integration: IntegrationCardData;
  setup: IntegrationSetup;
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Credential setup for an integration, in place over the marketplace (Figma "Setup/Connection Flow":
 * Setup → Review → Loading → toast). A connected integration opens locked until the client confirms
 * they want to replace the saved details; the API never returns them, so an update starts blank.
 */
export default function IntegrationSetupDrawer({ integration, setup, isOpen, onClose }: IntegrationSetupDrawerProps) {
  const { id, name, logo, group, state } = integration;
  const { t } = useTranslation('translations', { keyPrefix: 'Integrations.IntegrationSetupDrawer' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const groupLabels = useIntegrationGroupLabels();
  const { saving, saveCredentials } = useSaveIntegrationCredentials();
  const isConnected = state === 'connected';
  const initialStep: SetupStep = isConnected ? 'locked' : 'edit';
  const [step, setStep] = useState<SetupStep>(initialStep);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);

  const { ctx, renderFormElements } = useIntegrationSetupFormConfig({
    setup,
    isDisabled: step === 'locked',
    isReviewing: step === 'review',
    onEnter: requestReview,
    onSubmit: handleSubmit,
  });

  function renderToastMessage(icon: 'check_circle' | 'error', message: ReactNode) {
    return (
      <span className={cn('toast')}>
        <MaterialSymbol icon={icon} size="20px" />
        {message}
      </span>
    );
  }

  async function handleSubmit(values: IntegrationSetupFormValues) {
    try {
      await saveCredentials(integration, values);

      toast.success(
        renderToastMessage(
          'check_circle',
          isConnected
            ? t('updatedToast', { defaultValue: '{{name}} has been updated successfully', name })
            : t('connectedToast', { defaultValue: '{{name}} has been connected successfully', name }),
        ),
      );

      handleClose();
    } catch {
      // The hook has logged the reason; the Figma error toast is the generic one, shown over the catalogue
      toast.neutral(
        renderToastMessage('error', t('saveError', { defaultValue: 'Something went wrong, please try again' })),
        { textColor: '#e8160b' },
      );

      handleClose();
    }
  }

  // Every field filled with more than whitespace and no validation error — computed directly rather than
  // through the debounced hasPendingData so Review (and Enter) react to the last keystroke
  const canReview = ctx.isValid && setup.fields.every((field) => (ctx.values[field.name] ?? '').trim() !== '');

  function requestReview() {
    if (step === 'edit' && canReview) setStep('review');
  }

  // Enter in a field submits the form; while editing that means "go to review", never a save
  function handleFormSubmit(event: FormEvent<HTMLFormElement>) {
    if (step !== 'review') {
      event.preventDefault();
      requestReview();

      return;
    }

    ctx.handleSubmit(event);
  }

  // Closing discards unsaved input, whichever way the drawer was dismissed
  function handleClose() {
    ctx.resetForm();
    setStep(initialStep);
    onClose();
  }

  async function handleUpdateDetailsClick() {
    const result = await alertDialog.caution(
      t('editWarningTitle', { defaultValue: 'You are about to edit your current integration' }),
      t('editWarningDescription', {
        defaultValue: 'This action cannot be reversed and will replace your current details.',
      }),
      {
        abortLabel: tGlobal('Buttons.goBack'),
        confirmLabel: t('editCurrentDetails', { defaultValue: 'Edit current details' }),
        confirmColorScheme: 'destructive',
        className: cn('editWarning'),
      },
    );

    if (result === 'confirm') setStep('edit');
  }

  // Distinct elements per step: if the same button morphed from Review into type="submit" during
  // the click that changed the step, the browser would submit the form on that very click
  function renderPrimaryAction() {
    if (step === 'locked') {
      return (
        <Button key="update" type="button" colorScheme="primary" onClick={() => void handleUpdateDetailsClick()}>
          {t('updateDetails', { defaultValue: 'Update Details' })}
        </Button>
      );
    }

    if (step === 'edit') {
      return (
        <Button key="review" type="button" colorScheme="primary" disabled={!canReview} onClick={requestReview}>
          {tGlobal('Buttons.review')}
        </Button>
      );
    }

    return (
      <Button key="connect" colorScheme="primary" type="submit" form={SETUP_FORM_ID} disabled={saving}>
        {t('connect', { defaultValue: 'Connect' })}
      </Button>
    );
  }

  return (
    <>
      <SideDrawer
        isOpen={isOpen}
        onClose={handleClose}
        // Figma "Drawer Header": logo, then the name as the drawer's h3 (H3/Large Title 24), then the category
        title={name}
        className={cn('card')}
        headerClassName={cn('header')}
        contentClassName={cn('content')}
        eyebrow={<img className={cn('logo')} src={logo} alt="" />}
        subtitle={<span className={cn('group')}>{groupLabels[group]}</span>}
        footer={
          <div className={cn('footer')}>
            <Button colorScheme="secondary" disabled={saving} onClick={handleClose}>
              {tGlobal('Buttons.cancel')}
            </Button>

            {renderPrimaryAction()}
          </div>
        }
      >
        <form
          id={SETUP_FORM_ID}
          className={cn('body')}
          data-testid={`integration-setup-${id}`}
          data-step={step}
          onSubmit={handleFormSubmit}
        >
          <Title level="4">{t('setupTitle', { defaultValue: 'Setup {{name}} integration', name })}</Title>
          {renderFormElements()}

          <UtilButton
            type="button"
            iconLeft="help"
            className={cn('howItWorksLink')}
            onClick={() => setIsHowItWorksOpen(true)}
          >
            {t('howItWorks', { defaultValue: 'How it works' })}
          </UtilButton>
        </form>

        {saving && (
          <div
            className={cn('savingOverlay')}
            role="status"
            aria-label={t('saving', { defaultValue: 'Saving…' })}
            data-testid="integration-setup-saving"
          >
            <MaterialSymbol icon="progress_activity" size="48px" className={cn('spinner')} />
          </div>
        )}
      </SideDrawer>

      {setup.howItWorks.kind === 'workflows' ? (
        <IntegrationWorkflowsDialog
          howItWorks={setup.howItWorks}
          isOpen={isHowItWorksOpen}
          onClose={() => setIsHowItWorksOpen(false)}
        />
      ) : (
        <IntegrationHowItWorksDialog
          howItWorks={setup.howItWorks}
          isOpen={isHowItWorksOpen}
          onClose={() => setIsHowItWorksOpen(false)}
        />
      )}
    </>
  );
}
